import { AHSeparator, Box, ListItem, SectionHeader, useBoxStyle } from "arkh";
import React, { SetStateAction, useCallback, useMemo, useState } from "react";
import { TeamSelectPopover } from "widgets/src/TeamSelectPopover";
import { graphql, useFragment } from "react-relay";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { ScrollView } from "react-native";
import { TodoEditNewMainTeamsComponent } from "../../../../TodoEdit/TodoEditNewMain/TodoEditNewMainTeams";
import { FormattedMessage } from "react-intl";
import { WidgetTodoOverviewSettings_Store$key } from "./__generated__/WidgetTodoOverviewSettings_Store.graphql";

type Props = {
  teamIds: string[];
  setTeamIds(_: SetStateAction<string[]>): void;
  store: WidgetTodoOverviewSettings_Store$key;
  displayMode: "MEDIUM" | "MEDIUM_GROUPED";
  setDisplayMode: (displayMode: "MEDIUM" | "MEDIUM_GROUPED") => void;
};

export function WidgetTodoOverviewSettings(props: Props) {
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const data = useFragment(
    graphql`
      fragment WidgetTodoOverviewSettings_Store on Store {
        publicTeams {
          id
          icon
          name
          color
          isPrivate
          ...TeamSelectList_Team
        }
      }
    `,
    props.store
  );

  const onItemPress = useCallback(
    (itemId: string, selected: boolean) => {
      if (selected) {
        props.setTeamIds((ids) => ids.filter((id) => id !== itemId));
      } else if (data.publicTeams.length - 1 === props.teamIds.length) {
        props.setTeamIds([]);
      } else {
        props.setTeamIds((ids) => [...ids, itemId]);
      }
    },
    [data.publicTeams.length, props]
  );

  const intl_tokens = useIntlTokens();
  const contentContainerStyle = useBoxStyle({
    spaceHorizontal: "medium",
    spaceVertical: "medium",
  });
  const [teamsModalVisible, setTeamsModalVisible] = useState(false);
  const selectedTeams = useMemo(
    () => data.publicTeams.filter((team) => props.teamIds.includes(team.id)),
    [data.publicTeams, props.teamIds]
  );

  return (
    <Box style={{ flex: 1 }}>
      {!isCompact ? <WAFakeHeader title={intl_tokens.SelectTeams} /> : null}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={contentContainerStyle}
      >
        <SectionHeader>
          <SectionHeader.Title>
            <FormattedMessage
              defaultMessage="Filter"
              description="WidgetTodoOverviewSettings Filter section title"
            />
          </SectionHeader.Title>
        </SectionHeader>
        <Box
          color="surface"
          cornerRadius="large"
          style={{ borderWidth: 1 }}
          borderColor="border-primary"
        >
          <TodoEditNewMainTeamsComponent
            allTeams={props.teamIds.length === 0}
            teams={selectedTeams}
            onPress={() => setTeamsModalVisible(true)}
          />
        </Box>
        <Box spaceTop="medium" />
        <SectionHeader>
          <SectionHeader.Title>
            <FormattedMessage
              defaultMessage="List Style"
              description="WidgetTodoOverviewSettings List Style section title"
            />
          </SectionHeader.Title>
        </SectionHeader>
        <ListItem
          isFirst
          border="border-primary"
          right={props.displayMode === "MEDIUM" ? <ListItem.Check /> : null}
          onPress={() => {
            props.setDisplayMode("MEDIUM");
          }}
        >
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Default"
              description="WidgetTodoOverview display mode MEDIUM"
            />
          </ListItem.Title>
        </ListItem>
        <AHSeparator />
        <ListItem
          isLast
          border="border-primary"
          right={
            props.displayMode === "MEDIUM_GROUPED" ? <ListItem.Check /> : null
          }
          onPress={() => {
            props.setDisplayMode("MEDIUM_GROUPED");
          }}
        >
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Grouped by sender"
              description="WidgetTodoOverview display mode MEDIUM_GROUPED"
            />
          </ListItem.Title>
        </ListItem>
      </ScrollView>
      <TeamSelectPopover
        onClose={() => {
          setTeamsModalVisible(false);
        }}
        onItemPress={onItemPress}
        onSelectAll={() => {
          props.setTeamIds([]);
        }}
        teams={data.publicTeams}
        teamIds={props.teamIds}
        visible={teamsModalVisible}
      />
    </Box>
  );
}
